import { useCallback, useEffect, useState } from 'react'
import { ChevronDown, ChevronUp, Loader2, Package } from 'lucide-react'

import { fetchCustomerPackageBalances } from '@/lib/api/packages'
import { apiErrorMessage } from '@/lib/apiErrorMessage'
import { getAssignedCustomer } from '@/components/pos/posCustomer'
import {
  countRedeemablePackageBalances,
  formatPackageExpiryLabel,
  packageBalanceTicketNote,
} from '@/lib/pos/posCustomerPackageBalances'
import { posPackageTypeLabel } from '@/lib/pos/posPackage'
import { cn } from '@/lib/utils'

/**
 * @param {object} props
 * @param {import('@/types/api').Sale | null} props.sale
 * @param {string | number} [props.refreshToken]
 * @param {boolean} [props.disabled]
 * @param {boolean} [props.compact]
 * @param {() => void} [props.onRedeem]
 */
export function PosCustomerPackageBalances({
  sale,
  refreshToken = '',
  disabled = false,
  compact = false,
  onRedeem,
}) {
  const customer = getAssignedCustomer(sale)
  const [packages, setPackages] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [openPackageId, setOpenPackageId] = useState(null)

  const loadBalances = useCallback(async () => {
    if (!customer?.id) {
      setPackages([])
      setError('')
      return
    }

    setLoading(true)
    setError('')

    try {
      const items = await fetchCustomerPackageBalances(customer.id)
      setPackages(items)
    } catch (err) {
      setPackages([])
      setError(apiErrorMessage(err, 'Unable to load package balances.'))
    } finally {
      setLoading(false)
    }
  }, [customer?.id])

  useEffect(() => {
    loadBalances()
  }, [loadBalances, refreshToken])

  if (!customer) return null

  const redeemableCount = countRedeemablePackageBalances(packages)

  if (compact) {
    return (
      <div className="pos-v2-packages-strip">
        <div className="flex items-center justify-between gap-2">
          <span className="flex min-w-0 items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-violet-800">
            <Package className="h-3.5 w-3.5 shrink-0" aria-hidden />
            <span>Active packages</span>
            {!loading && packages.length > 0 ? (
              <span className="rounded-full bg-violet-100 px-1.5 py-px text-[9px] font-bold text-violet-700">
                {packages.length}
              </span>
            ) : null}
          </span>
          {redeemableCount > 0 && onRedeem ? (
            <button
              type="button"
              disabled={disabled}
              onClick={onRedeem}
              className="shrink-0 rounded-md border border-emerald-300 bg-emerald-50 px-2 py-0.5 text-[9px] font-bold uppercase text-emerald-800 hover:bg-emerald-100 disabled:opacity-50"
            >
              Redeem
            </button>
          ) : null}
        </div>

        {loading ? (
          <p className="mt-1 flex items-center gap-1.5 text-[10px] text-slate-500">
            <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
            Loading packages…
          </p>
        ) : error ? (
          <p className="mt-1 text-[10px] text-destructive">{error}</p>
        ) : packages.length === 0 ? (
          <p className="mt-1 text-[10px] text-slate-500">No active packages for this guest.</p>
        ) : (
          <ul className="pos-v2-packages-list mt-1 space-y-1">
            {packages.map((pkg) => {
              const expiry = formatPackageExpiryLabel(pkg.expires_at)
              const detailsOpen = openPackageId === pkg.customer_package_id

              return (
                <li key={pkg.customer_package_id} className="rounded-md border border-violet-200/80 bg-white/80">
                  <button
                    type="button"
                    disabled={disabled}
                    className="flex w-full items-center gap-2 px-2 py-1 text-left"
                    onClick={() => setOpenPackageId(detailsOpen ? null : pkg.customer_package_id)}
                    aria-expanded={detailsOpen}
                  >
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-[11px] font-semibold text-slate-900">{pkg.package_name}</p>
                      <p className="truncate text-[10px] text-slate-600">{pkg.headline}</p>
                    </div>
                    <div className="flex shrink-0 items-center gap-1">
                      {pkg.is_redeemable ? (
                        <span className="rounded bg-emerald-100 px-1 py-px text-[8px] font-bold uppercase text-emerald-800">
                          OK
                        </span>
                      ) : null}
                      {expiry ? (
                        <span className="max-w-[4.5rem] truncate text-[8px] text-amber-700">{expiry}</span>
                      ) : null}
                      {detailsOpen ? (
                        <ChevronUp className="h-3 w-3 text-slate-400" aria-hidden />
                      ) : (
                        <ChevronDown className="h-3 w-3 text-slate-400" aria-hidden />
                      )}
                    </div>
                  </button>
                  {detailsOpen && pkg.details.length > 0 ? (
                    <ul className="border-t border-violet-100 px-2 py-1">
                      {pkg.details.map((row) => (
                        <li
                          key={`${pkg.customer_package_id}-${row.label}`}
                          className="flex justify-between gap-2 py-0.5 text-[9px]"
                        >
                          <span className="text-slate-500">{row.label}</span>
                          <span className="font-medium text-slate-800">{row.value}</span>
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </li>
              )
            })}
          </ul>
        )}
      </div>
    )
  }

  return (
    <div className="overflow-hidden rounded-lg border border-border/50 bg-card">
      <div className="flex items-center justify-between gap-2 border-b border-border/40 bg-muted/20 px-2.5 py-2">
        <span className="flex min-w-0 items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
          <Package className="h-3.5 w-3.5 shrink-0 text-primary" aria-hidden />
          <span className="truncate">Active packages</span>
          {packages.length > 0 ? (
            <span className="rounded-full bg-primary/10 px-1.5 py-0.5 text-[9px] font-bold text-primary">
              {packages.length}
            </span>
          ) : null}
        </span>
        {redeemableCount > 0 ? (
          <span className="shrink-0 rounded-full bg-emerald-100 px-1.5 py-0.5 text-[9px] font-semibold uppercase text-emerald-800">
            {redeemableCount} redeemable
          </span>
        ) : null}
      </div>

      <div className="p-2">
        {loading ? (
          <div className="flex items-center gap-2 py-3 text-[11px] text-muted-foreground">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            Loading balances…
          </div>
        ) : error ? (
          <p className="py-2 text-[11px] text-destructive">{error}</p>
        ) : packages.length === 0 ? (
          <p className="py-2 text-[11px] text-muted-foreground">
            No active package balances for this guest.
          </p>
        ) : (
          <ul className="space-y-1.5">
            {packages.map((pkg) => {
              const expiry = formatPackageExpiryLabel(pkg.expires_at)
              const ticketNote = packageBalanceTicketNote(pkg, sale)
              const detailsOpen = openPackageId === pkg.customer_package_id

              return (
                <li
                  key={pkg.customer_package_id}
                  className="rounded-md border border-border/50 bg-muted/10"
                >
                  <button
                    type="button"
                    disabled={disabled}
                    className="w-full px-2 py-1.5 text-left"
                    onClick={() => setOpenPackageId(
                      detailsOpen ? null : pkg.customer_package_id,
                    )}
                    aria-expanded={detailsOpen}
                  >
                    <div className="flex items-start gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
                          <p className="text-[11px] font-semibold leading-tight text-foreground">
                            {pkg.package_name}
                          </p>
                          <span className="text-[9px] font-semibold uppercase tracking-wide text-primary">
                            {posPackageTypeLabel(pkg.package_type)}
                          </span>
                        </div>
                        <p className="mt-0.5 text-[10px] leading-snug text-foreground/90">
                          {pkg.headline}
                        </p>
                        <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[9px]">
                          {expiry ? (
                            <span className={cn(
                              expiry.includes('Expires in') || expiry === 'Expires today'
                                ? 'font-medium text-amber-700'
                                : 'text-muted-foreground',
                            )}
                            >
                              {expiry}
                            </span>
                          ) : null}
                          {ticketNote ? (
                            <span className="font-medium text-amber-700">{ticketNote}</span>
                          ) : null}
                        </div>
                      </div>
                      <div className="flex shrink-0 flex-col items-end gap-1">
                        {pkg.is_redeemable ? (
                          <span className="rounded-full bg-emerald-100 px-1.5 py-0.5 text-[8px] font-semibold uppercase text-emerald-800">
                            Redeem
                          </span>
                        ) : null}
                        {detailsOpen ? (
                          <ChevronUp className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
                        ) : (
                          <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
                        )}
                      </div>
                    </div>
                  </button>

                  {detailsOpen && pkg.details.length > 0 ? (
                    <ul className="border-t border-border/40 bg-background/60 px-2 py-1.5">
                      {pkg.details.map((row) => (
                        <li
                          key={`${pkg.customer_package_id}-${row.label}`}
                          className="grid grid-cols-[minmax(0,1fr)_auto] gap-2 py-0.5 text-[10px]"
                        >
                          <span className="text-muted-foreground">{row.label}</span>
                          <span className="font-medium text-foreground">{row.value}</span>
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </li>
              )
            })}
          </ul>
        )}

        {redeemableCount > 0 && onRedeem ? (
          <button
            type="button"
            disabled={disabled}
            onClick={onRedeem}
            className="mt-2 w-full rounded-md border border-primary/30 bg-primary/5 px-2 py-1.5 text-[10px] font-semibold text-primary transition-colors hover:bg-primary/10 disabled:opacity-50"
          >
            F7 — Redeem covered service
          </button>
        ) : null}
      </div>
    </div>
  )
}
